import { motion } from 'framer-motion'
import { TickCircle } from 'iconsax-react'

import { Skeleton } from '@/components/ui/skeleton'
import type { ISubjectIdentity } from '@/hooks/useReportData'

interface SubjectHeaderProps {
  subject: ISubjectIdentity | null
  summary: string | null
}

export function SubjectHeader({ subject, summary }: SubjectHeaderProps) {
  if (!subject) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton className="w-12 h-12 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="w-40 h-5" />
            <Skeleton className="w-24 h-3" />
          </div>
        </div>
      </div>
    )
  }

  const fullName = [subject.firstName, subject.lastName]
    .filter(Boolean)
    .join(' ')

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-3"
    >
      <div className="flex items-center gap-3">
        {subject.imageUrl ? (
          <img
            src={subject.imageUrl}
            alt={fullName}
            className="w-16 h-16 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-neutralColor30 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl text-neutralColor60 font-medium">
              {(subject.firstName?.[0] || '').toUpperCase()}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-medium text-neutralColor truncate">
              {fullName}
            </h2>
            <TickCircle
              size={20}
              variant="Bold"
              className="text-primaryColor flex-shrink-0"
            />
          </div>
        </div>
      </div>

      {summary && (
        <p className="text-xs text-neutralColor60 leading-relaxed">{summary}</p>
      )}
    </motion.div>
  )
}
